import { Listbox, Transition } from '@headlessui/react';
import clsx from 'clsx';

//type
import type {
    SelectComponentProps,
    OptionType,
    SelectedOptionHelper
} from '@/components/base/Select/SelectComponent/types';

//config
import {
    selectPlacement,
    optionWrapperSizeStyle
} from '@/components/base/Select/SelectComponent/selectConfig';

//components
import { SelectOption } from '@/components/base/Select/SelectComponent/SubComponents/SelectOption';
import { SelectLabel } from '@/components/base/Select/SelectComponent/SubComponents/SelectLabel';
import { SelectButton } from '@/components/base/Select/SelectComponent/SubComponents/SelectButton';
import FormFieldMessage from '@/components/base/FormFieldMessage';

export function SelectComponent<
    TOption extends OptionType,
    TSelected extends SelectedOptionHelper<TOption, TMultiple>,
    TMultiple extends boolean,
    TSetSelected extends (args: TSelected) => void
>({
    label,
    options,
    button,
    children,
    required,
    className,
    disabled,
    selected,
    setSelected,
    error,
    buttonVariant = 'default',
    panelOptions = {
        size: 'default',
        placement: 'bottom',
        isTruncate: true
    },
    layoutOptions = {
        hasChevron: true
    },
    ...restProps
}: SelectComponentProps<TOption, TSelected, TMultiple, TSetSelected>) {
    return (
        <div className={clsx('w-full', className)} {...restProps}>
            <Listbox value={selected} onChange={setSelected} multiple={layoutOptions.isMultiple}>
                {({ open }) => (
                    <>
                        <SelectLabel label={label} required={required} />
                        <div className="relative">
                            <SelectButton
                                button={button}
                                selected={selected}
                                disabled={disabled}
                                error={error}
                                buttonVariant={buttonVariant}
                                size={panelOptions.size}
                                forcedDisplay={layoutOptions.forcedDisplay}
                                isMultiple={layoutOptions.isMultiple}
                                hasChevron={layoutOptions.hasChevron}
                            />
                            <Transition
                                show={open && !disabled}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0">
                                <Listbox.Options
                                    static
                                    className={clsx(
                                        'scrollbar absolute z-20 max-h-60 overflow-auto rounded-md bg-white px-1 text-icoGray-500 shadow-lg focus:outline-none dark:bg-icoDarkMode-anthracite',
                                        selectPlacement[panelOptions.placement],
                                        optionWrapperSizeStyle[panelOptions.size],
                                        {
                                            'w-56': layoutOptions.noValue,
                                            '!w-full': !layoutOptions.noValue,
                                            'py-0 pb-1': layoutOptions.hasFilter,
                                            'py-1': !layoutOptions.hasFilter,
                                            'ring-1 ring-gray-900 ring-opacity-20 dark:ring-icoDarkMode-beton':
                                                !layoutOptions.noBorder
                                        },
                                        panelOptions.optionsWrapperClassName
                                    )}>
                                    <SelectOption
                                        isTruncate={panelOptions.isTruncate}
                                        forcedDisplay={layoutOptions.forcedDisplay}
                                        size={panelOptions.size}
                                        options={options}
                                        isMultiple={layoutOptions.isMultiple}
                                        selected={selected}>
                                        {children}
                                    </SelectOption>
                                </Listbox.Options>
                            </Transition>
                        </div>
                    </>
                )}
            </Listbox>
            {error && typeof error === 'string' && !disabled ? (
                <FormFieldMessage>{error}</FormFieldMessage>
            ) : null}
        </div>
    );
}
